import { useState } from 'react';
import {
  EXPORT_PERIODS,
  exportRange,
  type DataExportRequest,
  type ExportDomain,
  type ExportFormat,
  type ExportPeriod,
} from '@forecastify/shared';
import { Button, Dialog, Segmented, Select } from '@forecastify/ui';
import { useT, type Translate } from '../../i18n';
import { Field } from '../card';
import { DateField } from '../date-field';
import { saveTextFile } from '../file-helpers';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The one export dialog — `markup.html` 2854–2900. C17, decision T-10.       │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **ONE DIALOG FOR EVERY EXPORT ARROW IN THE PRODUCT, WHICH IS WHAT T-10 ASKED FOR.** The mockup
 * has nine of them, each with its own toast; five chunks met one and left a note saying C17 owns it.
 * A screen opens this with a domain and gets a file or a queued email — it does not decide which, and
 * it does not build a CSV.
 *
 * ⚠️ **The period is hidden for a domain with no date to filter on.** Three of the six have none — a
 * client list is not a period — and the mockup only shows the range for its two dated domains.
 */

/** Which domains a period actually narrows. */
const DATED: readonly ExportDomain[] = ['task', 'worklog'];

function periodLabel(t: Translate, period: ExportPeriod): string {
  /* One `t()` per branch with a literal key. */
  if (period === 'week') return t('export.period.week');
  if (period === 'lastweek') return t('export.period.lastweek');
  if (period === 'month') return t('export.period.month');
  if (period === 'lastmonth') return t('export.period.lastmonth');
  if (period === 'quarter') return t('export.period.quarter');
  if (period === 'all') return t('export.period.all');
  return t('export.period.custom');
}

export function domainLabel(t: Translate, domain: ExportDomain): string {
  if (domain === 'task') return t('export.domain.task');
  if (domain === 'worklog') return t('export.domain.worklog');
  if (domain === 'project') return t('export.domain.project');
  if (domain === 'client') return t('export.domain.client');
  if (domain === 'member') return t('export.domain.member');
  return t('export.domain.report');
}

export interface ExportDialogProps {
  /** `null` closes it. The domain is what the calling screen exports. */
  readonly domain: ExportDomain | null;
  /** For `domain: 'report'`. */
  readonly reportId?: string | null;
  /** Narrows a task or worklog export the way the screen was narrowed. */
  readonly projectId?: string | null;
  /** The rows a bulk action selected. Null exports the whole list — see the contract. */
  readonly ids?: readonly string[] | null;
  /**
   * ┌───────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ C48 · A RANGE AND TWO IDS THE CALLER ALREADY WORKED OUT. R-34.   │
   * └───────────────────────────────────────────────────────────────────────┘
   *
   * The Agent's export offer resolves *"August's hours on Acme"* into a filter, and the person
   * confirms it **here** — in the dialog they already know, with the values filled in. Null from
   * every other caller, which leaves the default range below in charge.
   */
  readonly from?: string | null;
  readonly to?: string | null;
  readonly contractId?: string | null;
  readonly memberId?: string | null;
  readonly today: string;
  readonly busy: boolean;
  readonly serverError: string | null;
  readonly onClose: () => void;
  readonly onRun: (request: DataExportRequest) => void;
}

export function ExportDialog(props: ExportDialogProps) {
  const t = useT();
  const { domain } = props;

  const [period, setPeriod] = useState<ExportPeriod>('month');
  /* 17 Oct 2026 · the mockup's CSV / XLSX choice, now that a real XLSX can be written. */
  const [format, setFormat] = useState<ExportFormat>('csv');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  /**
   * ⚠️ Re-seeded whenever a different domain arrives, so the dialog does not show the last one's
   * range — the same guard the wizard and the worklog entry dialog both carry.
   *
   * ┌───────────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ C48 · THE KEY IS THE WHOLE SEED, NOT JUST THE DOMAIN — OTHERWISE AN  │
   * │ AGENT OFFER OPENED AFTER A PLAIN EXPORT OF THE SAME DOMAIN WOULD SHOW     │
   * │ THE PLAIN ONE'S RANGE.                                                    │
   * └───────────────────────────────────────────────────────────────────────────┘
   *
   * ⚠️ **Existing callers are unaffected, and that is why it is the seed rather than a counter.**
   * Every screen but the Agent passes no range, so its key is `worklog||…` on every open and this
   * still re-seeds exactly when the domain changes. A counter bumped per `open()` would have reset
   * the range each time somebody reopened the same dialog, which is a behaviour change nobody asked
   * for.
   *
   * ⚠️ **A supplied range makes the period `custom`**, because that is what it is: naming it *this
   * month* over somebody else's two dates is the same lie the `DateField` handlers below avoid.
   */
  const seed = [domain, props.from ?? '', props.to ?? '', props.projectId ?? ''].join('|');
  const [seededFor, setSeededFor] = useState<string | null>(null);
  if (domain !== null && seededFor !== seed) {
    setSeededFor(seed);
    if (props.from !== undefined && props.from !== null) {
      setPeriod('custom');
      setFrom(props.from);
      setTo(props.to ?? '');
    } else {
      const range = exportRange('month', props.today);
      setPeriod('month');
      setFrom(range.from ?? '');
      setTo(range.to ?? '');
    }
  }

  if (domain === null) return null;
  const dated = DATED.includes(domain);

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) props.onClose();
      }}
      /* ⚠️ **Item 28 · no `description`.** The lede restated what a dialog titled *"Feladatok
         exportálása"* already says, above a form whose every field is labelled. */
      title={t('export.title', { what: domainLabel(t, domain) })}
      closeLabel={t('common.close')}
    >
      <div className="flex flex-col gap-3">
        {dated ? (
          <>
            <Field label={t('export.field.period')}>
              <Select
                value={period}
                onChange={(event) => {
                  const next = event.target.value as ExportPeriod;
                  setPeriod(next);
                  /* ⚠️ A preset fills the two dates; `custom` leaves whatever is showing, because
                   * clearing them would throw away what somebody just typed. */
                  const range = exportRange(next, props.today);
                  if (next !== 'custom') {
                    setFrom(range.from ?? '');
                    setTo(range.to ?? '');
                  }
                }}
                options={EXPORT_PERIODS.map((value) => ({
                  value,
                  label: periodLabel(t, value),
                }))}
              />
            </Field>

            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t('export.field.from')}>
                <DateField
                  label={t('export.field.from')}
                  value={from}
                  onChange={(next) => {
                    setFrom(next);
                    /* ⚠️ Picking a date makes the period custom — the mockup's own behaviour, and
                     * without it the label would claim "this month" over a range that is not. */
                    setPeriod('custom');
                  }}
                />
              </Field>
              <Field label={t('export.field.to')}>
                <DateField
                  label={t('export.field.to')}
                  value={to}
                  onChange={(next) => {
                    setTo(next);
                    setPeriod('custom');
                  }}
                />
              </Field>
            </div>
          </>
        ) : (
          /* ⚠️ Said rather than left blank: a dialog with one control and no explanation reads as a
             form that failed to load. */
          <p className="text-muted-foreground text-[12px] leading-relaxed">
            {t('export.noPeriod', { what: domainLabel(t, domain) })}
          </p>
        )}

        {/* 17 Oct 2026 · the mockup's format choice — `write-excel-file` writes a real XLSX. */}
        <Field label={t('export.format')}>
          <Segmented
            value={format}
            label={t('export.format')}
            options={[
              { value: 'csv', label: 'CSV' },
              { value: 'xlsx', label: 'XLSX' },
            ]}
            onChange={setFormat}
            className="self-start"
          />
        </Field>

        {props.serverError !== null && (
          <p role="alert" className="text-destructive text-[12px]">
            {props.serverError}
          </p>
        )}

        <div className="flex items-center gap-2">
          <div className="flex-1" />
          <Button variant="outline" onClick={props.onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={props.busy}
            onClick={() => {
              props.onRun({
                domain,
                format,
                /* ⚠️ An empty date is null on the wire, and `all` sends neither — a range of `''`
                 * would be a validation error about a field somebody deliberately left empty. */
                from: dated && from !== '' && period !== 'all' ? from : null,
                to: dated && to !== '' && period !== 'all' ? to : null,
                reportId: props.reportId ?? null,
                projectId: props.projectId ?? null,
                /* ⚠️ Null unless a caller worked one out. A period's contract-narrowed export is
                   run from the period itself rather than from here; C48's Agent offer is the other
                   caller that has a contract, and it resolves one from what somebody asked for. */
                contractId: props.contractId ?? null,
                ids: props.ids === undefined || props.ids === null ? null : [...props.ids],
                /* ⚠️ **The export service ignores this whenever the caller's read is not
                   workspace-wide**, which is what keeps *"export Anna's hours"* from widening
                   anything. Passed through rather than filtered here: the trim is the server's. */
                memberId: props.memberId ?? null,
              });
            }}
          >
            {props.busy ? t('export.running') : t('export.run')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

/**
 * Save a CSV the browser already has.
 *
 * ⚠️⚠️ **A BLOB AND AN OBJECT URL, revoked afterwards.** A `data:` URL of a large CSV hits a length
 * limit in some browsers and silently produces a broken download; an object URL that is never revoked
 * holds the whole file in memory for the life of the tab.
 *
 * ⚠️ **`text/csv;charset=utf-8`, and the string already carries a BOM** — `toCsv` puts it there so
 * Excel on Windows does not read a Hungarian export as its system codepage.
 */
export function saveCsv(filename: string, csv: string): void {
  /* ⚠️ The mechanism moved to `file-helpers.ts` in C48, when the Agent's reply became a third caller
   * for the same five lines. The BOM is NOT lost with it: `toCsv` writes one on the server, into the
   * string — see that helper's header, which argues why delivery is the wrong layer for it. */
  saveTextFile(filename, csv, 'text/csv;charset=utf-8');
}
